import { Head } from '@inertiajs/react';

interface SEOProps {
  title?: string;
  description?: string;
  canonical?: string;
  ogImage?: string;
  ogImageWidth?: number;
  ogImageHeight?: number;
  ogImageAlt?: string;
  ogType?: string;
  twitterCard?: 'summary' | 'summary_large_image' | 'app' | 'player';
  noIndex?: boolean;
  structuredData?: object | object[];
  children?: React.ReactNode;
}

export default function SEO({
  title = 'Roller Coaster Gifts - Gifts for Lovers of Roller Coasters and Theme Parks',
  description = 'Curated gifts, stores, and more from around the web for Roller Coaster and Theme Park enthusiasts and friends. Explore now View All Gifts View All Stores View…',
  canonical = 'https://rollercoastergifts.com/',
  ogImage = 'https://i0.wp.com/rollercoastergifts.com/wp-content/uploads/2024/12/roller-coaster.jpg?fit=1920%2C1280&amp;ssl=1',
  ogImageWidth = 1920,
  ogImageHeight = 1280,
  ogImageAlt = 'roller coaster train traveling down a steep hill with the sky as a backdrop',
  ogType = 'website',
  twitterCard = 'summary_large_image',
  noIndex = false,
  structuredData,
  children,
}: SEOProps) {
  const robots = noIndex
    ? 'noindex,nofollow'
    : 'max-snippet:-1,max-image-preview:large,max-video-preview:-1';

  // Handle structured data - can be single object or array
  const structuredDataArray = structuredData
    ? Array.isArray(structuredData)
      ? structuredData
      : [structuredData]
    : [];

  return (
    <Head>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="theme-color" content="#8dcbe6" />
      <meta name="author" content="Roller Coaster Gifts" />

      {/* Open Graph (Facebook, LinkedIn, etc.) */}
      <meta property="og:type" content={ogType} />
      <meta property="og:locale" content="en_US" />
      <meta property="og:site_name" content="Roller Coaster Gifts" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content={ogImageWidth.toString()} />
      <meta property="og:image:height" content={ogImageHeight.toString()} />
      <meta property="og:image:alt" content={ogImageAlt} />

      {/* LinkedIn specific tags */}
      <meta property="og:image:secure_url" content={ogImage} />

      {/* Twitter Cards */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={ogImageAlt} />

      {/* SEO */}
      <meta name="robots" content={robots} />
      <link rel="canonical" href={canonical} />

      {/* Structured Data */}
      {structuredDataArray.map((data, index) => (
        <script
          key={index}
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(data),
          }}
        />
      ))}

      {children}
    </Head>
  );
}
